import { Slot } from "../../shared/types";
import { useState } from "react";
import Loading from "../Loading";

type AppointmentGroupSlotProps = {
  date: string;
  daySlots: Slot[];
  onSlotSelect: (slot: Slot) => void;
  loading: boolean;
};

const AppointmentGroupSlot = ({ date, daySlots, onSlotSelect, loading }: AppointmentGroupSlotProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateSidebar = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  const getDuration = (start: string, end: string) => {
    const startTime = new Date(start);
    const endTime = new Date(end);
    const diffMs = endTime.getTime() - startTime.getTime();
    const diffMinutes = Math.round(diffMs / (1000 * 60));
    return diffMinutes;
  };

  const groupSlotsByStartTime = (slots: Slot[]) => {
    const sortedSlots = [...slots].sort((a, b) => {
      const startTimeA = new Date(a.start).getTime();
      const startTimeB = new Date(b.start).getTime();

      if (startTimeA !== startTimeB) return startTimeA - startTimeB;

      const durationA = getDuration(a.start, a.end);
      const durationB = getDuration(b.start, b.end);
      return durationA - durationB;
    });

    const grouped = sortedSlots.reduce((groups, slot) => {
      const startDate = new Date(slot.start);
      const hour = startDate.getHours();
      const hourLabel = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), hour).toLocaleTimeString('en-US', {
        hour: 'numeric',
        hour12: true
      });

      if (!groups[hourLabel]) {
        groups[hourLabel] = [];
      }
      groups[hourLabel].push(slot);
      return groups;
    }, {} as Record<string, Slot[]>);

    return grouped;
  };

  const groupedSlots = groupSlotsByStartTime(daySlots);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <div className="flex">
        <div className="border-r border-gray-200 px-4 py-6 flex flex-col items-center justify-center min-w-[120px]">
          <div className="text-center">
            <div className="font-semibold text-gray-900 text-lg">
              {formatDateSidebar(date)}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {loading ? (
                <div className="flex justify-center mt-8">
                  <Loading message="Loading Slots..." />
                </div>
              ) : (
                <>
                  {daySlots.length} slot{daySlots.length !== 1 ? 's' : ''}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 p-4 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
          <div className="mb-4 flex items-center justify-end">
            <span className="text-gray-500 transition-transform duration-200">
              {isExpanded ? '▼' : '▶'}
            </span>
          </div>

          <div className={`transition-all duration-200 overflow-auto ${
            isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}>
            {Object.entries(groupedSlots).map(([startTime, slots]) => (
              <div key={startTime} className="mb-6">
                <h5 className="font-medium text-gray-700 text-base mb-3 border-b border-gray-200 pb-2">
                  {startTime}
                </h5>
                <div className="flex flex-wrap gap-2 justify-start">
                  {slots.map((slot, index) => (
                    <button
                      key={`slot-${slot.id}-${index}`}
                      onClick={() => onSlotSelect(slot)}
                      className="text-center border border-brand-200 rounded-lg px-6 py-4 text-brand-700 hover:bg-brand-50 hover:border-brand-300 transition-colors text-sm whitespace-nowrap"
                    >
                      <div>{formatTime(slot.start)} - {formatTime(slot.end)}</div>
                      <div className="text-xs text-gray-500">
                        {getDuration(slot.start, slot.end)} min
                      </div>
                      <div>{slot.serviceType?.text || slot.serviceType?.coding?.[0]?.display}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {!isExpanded && daySlots.length > 0 && (
            loading ? (
              <div className="flex justify-center mt-8">
                <Loading message="Loading Slots..." />
              </div>
            ) : (
              <div className="text-center text-gray-500 text-sm mt-2">
                Click to view {daySlots.length} available time{daySlots.length !== 1 ? 's' : ''}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentGroupSlot;
